/**
 * Normalize an arbitrary locale string to consistent BCP 47 form.
 *
 * - Replaces underscores with hyphens (`en_US` → `en-US`)
 * - Trims whitespace
 * - Bare language codes lowercased (`DE` → `de`)
 * - Multi-subtag inputs are delegated to `Intl.getCanonicalLocales()` — the
 *   canonical BCP 47 authority per ECMA-402. This correctly handles ALL
 *   position-1 disambiguation (region vs script vs variant), plus scripts,
 *   variants, extensions, and multi-subtag ordering:
 *     - `en-us` → `en-US` (region uppercase)
 *     - `zh-hant` → `zh-Hant` (script title-case, NOT `zh-HANT`)
 *     - `sl-rozaj` → `sl-rozaj` (variant stays lowercase, NOT `sl-ROZAJ`)
 *     - `zh-hant-tw` → `zh-Hant-TW`
 *     - `sl-rozaj-biske` → `sl-biske-rozaj` (variants alphabetized)
 *     - `en-US-U-CA-GREGORY` → `en-US-u-ca-gregory` (extension lowercase)
 * - Falls back to a lenient uppercase-past-position-0 rule for inputs Intl
 *   rejects (malformed variants, unknown structure). Preserves Joy's
 *   historical leniency for legacy inputs without violating spec on
 *   well-formed tags.
 *
 * Returns `null` for empty, whitespace-only, or null/undefined input.
 */
export function canonicalizeLocale(
  input: string | null | undefined,
): string | null {
  if (input === null || input === undefined) return null;

  const trimmed = input.trim();
  if (trimmed.length === 0) return null;

  const normalized = trimmed.replace(/_/g, '-');
  const parts = normalized.split('-');

  if (parts.length === 1) {
    // Bare language code: "de", "EN", "fr". Intl also handles this correctly
    // (returns lowercase for a valid language subtag), but keeping the direct
    // fast path avoids the Intl call for the highest-frequency shape.
    return parts[0].toLowerCase();
  }

  // Multi-subtag — delegate to Intl.getCanonicalLocales() for spec-compliant
  // casing across region / script / variant / extension positions. If Intl
  // rejects the input (malformed variant, unknown structure), fall back to
  // the lenient uppercase-past-position-0 rule that matches historical
  // behavior for legacy Joy inputs.
  try {
    const [canonical] = Intl.getCanonicalLocales(normalized);
    if (canonical !== undefined) return canonical;
  } catch {
    // fall through to lenient path
  }

  return [
    parts[0].toLowerCase(),
    ...parts
      .slice(1)
      .map((p) =>
        p.length === 4
          ? p[0].toUpperCase() + p.slice(1).toLowerCase()
          : p.toUpperCase(),
      ),
  ].join('-');
}
